'use client';

/**
 * ImageGallery Component
 *
 * A modern, mobile-first image gallery with:
 * - Main large image display
 * - Thumbnail navigation strip
 * - Swipe gesture support (touch devices)
 * - Keyboard navigation
 * - Smooth transitions
 * - Fullscreen lightbox mode
 *
 * @example
 * <ImageGallery
 *   images={['/img1.jpg', '/img2.jpg']}
 *   alts={['Image 1', 'Image 2']}
 *   title="Property Gallery"
 * />
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import styles from './ImageGallery.module.css';

const GALLERY_I18N = {
  en: {
    viewAll: (n: number) => `View all ${n} photos`,
    prevImages: 'Previous images',
    nextImages: 'More images',
    viewImage: (n: number) => `View image ${n}`,
    viewTitle: (t: string, n: number) => `View ${t} - Image ${n}`,
    viewMain: (t: string) => `View ${t} - Main image`,
    lightbox: 'Image lightbox',
    close: 'Close lightbox',
    prev: 'Previous image',
    next: 'Next image',
    goTo: (n: number) => `Go to image ${n}`,
  },
  es: {
    viewAll: (n: number) => `Ver las ${n} fotos`,
    prevImages: 'Ver imágenes anteriores',
    nextImages: 'Ver más imágenes',
    viewImage: (n: number) => `Ver imagen ${n}`,
    viewTitle: (t: string, n: number) => `Ver ${t} - Imagen ${n}`,
    viewMain: (t: string) => `Ver ${t} - Imagen principal`,
    lightbox: 'Galería de imágenes',
    close: 'Cerrar galería',
    prev: 'Imagen anterior',
    next: 'Imagen siguiente',
    goTo: (n: number) => `Ir a imagen ${n}`,
  },
  pt: {
    viewAll: (n: number) => `Ver todas as ${n} fotos`,
    prevImages: 'Ver imagens anteriores',
    nextImages: 'Ver mais imagens',
    viewImage: (n: number) => `Ver imagem ${n}`,
    viewTitle: (t: string, n: number) => `Ver ${t} - Imagem ${n}`,
    viewMain: (t: string) => `Ver ${t} - Imagem principal`,
    lightbox: 'Galeria de imagens',
    close: 'Fechar galeria',
    prev: 'Imagem anterior',
    next: 'Próxima imagem',
    goTo: (n: number) => `Ir para imagem ${n}`,
  },
};

interface ImageGalleryProps {
  /** Array of image URLs */
  images: string[];
  /** Array of alt texts (1:1 with images) */
  alts?: string[];
  /** Gallery title for accessibility */
  title?: string;
  /** Aspect ratio for main image (default: 16/10) */
  aspectRatio?: number;
  /** Locale for translations */
  locale?: 'en' | 'es' | 'pt';
}

export default function ImageGallery({
  images,
  alts = [],
  title = 'Gallery',
  aspectRatio = 16 / 10,
  locale = 'en',
}: ImageGalleryProps) {
  const i18n = GALLERY_I18N[locale] || GALLERY_I18N.en;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [slideDir, setSlideDir] = useState<'left' | 'right'>('left');
  const [slideKey, setSlideKey] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const mouseStartX = useRef<number | null>(null);
  const mouseEndX = useRef<number | null>(null);
  const hasDragged = useRef(false);
  const isFirstThumbScroll = useRef(true);
  const mainImageRef = useRef<HTMLDivElement>(null);
  const desktopThumbsRef = useRef<HTMLDivElement>(null);
  const lightboxThumbsRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLightboxOpen]);

  // Auto-scroll thumbnail strips to keep active thumb centered.
  // Skip the first run — scrollIntoView on mount drags the whole page down
  // to the gallery on lodge booking pages.
  useEffect(() => {
    if (isFirstThumbScroll.current) {
      isFirstThumbScroll.current = false;
      return;
    }
    [desktopThumbsRef.current, lightboxThumbsRef.current].forEach((container) => {
      if (!container) return;
      const thumb = container.children[currentIndex] as HTMLElement;
      if (!thumb) return;
      // Scroll only the thumb strip horizontally — never the page.
      const containerRect = container.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const delta = (thumbRect.left + thumbRect.width / 2) - (containerRect.left + containerRect.width / 2);
      container.scrollBy({ left: delta, behavior: 'smooth' });
    });
  }, [currentIndex]);

  // Desktop thumbnail strip carousel arrows
  const [thumbCanScrollLeft, setThumbCanScrollLeft] = useState(false);
  const [thumbCanScrollRight, setThumbCanScrollRight] = useState(false);

  const updateThumbScrollState = useCallback(() => {
    const strip = desktopThumbsRef.current;
    if (!strip) return;
    setThumbCanScrollLeft(strip.scrollLeft > 2);
    setThumbCanScrollRight(strip.scrollLeft < strip.scrollWidth - strip.clientWidth - 2);
  }, []);

  useEffect(() => {
    const strip = desktopThumbsRef.current;
    if (!strip) return;
    updateThumbScrollState();
    strip.addEventListener('scroll', updateThumbScrollState, { passive: true });
    const ro = new ResizeObserver(updateThumbScrollState);
    ro.observe(strip);
    return () => {
      strip.removeEventListener('scroll', updateThumbScrollState);
      ro.disconnect();
    };
  }, [updateThumbScrollState]);

  const scrollThumbs = useCallback((dir: 'left' | 'right') => {
    const strip = desktopThumbsRef.current;
    if (!strip) return;
    strip.scrollBy({ left: dir === 'right' ? 264 : -264, behavior: 'smooth' });
  }, []);

  const openLightbox = useCallback(() => {
    setIsLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  const navigateTo = useCallback(
    (newIndex: number, direction: 'left' | 'right') => {
      if (isTransitioning || newIndex === currentIndex) return;
      setSlideDir(direction);
      setSlideKey((k) => k + 1);
      setIsTransitioning(true);
      setCurrentIndex(newIndex);
      setTimeout(() => setIsTransitioning(false), 400);
    },
    [currentIndex, isTransitioning]
  );

  const goToIndex = useCallback(
    (index: number) => {
      navigateTo(index, index > currentIndex ? 'left' : 'right');
    },
    [currentIndex, navigateTo]
  );

  const goToPrevious = useCallback(() => {
    navigateTo(currentIndex === 0 ? images.length - 1 : currentIndex - 1, 'right');
  }, [currentIndex, images.length, navigateTo]);

  const goToNext = useCallback(() => {
    navigateTo(currentIndex === images.length - 1 ? 0 : currentIndex + 1, 'left');
  }, [currentIndex, images.length, navigateTo]);

  // Handle keyboard navigation — placed after nav functions to avoid temporal dead zone
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      if (e.key === 'ArrowLeft') goToPrevious();
      else if (e.key === 'ArrowRight') goToNext();
      else if (e.key === 'Escape') setIsLightboxOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, goToPrevious, goToNext]);

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [goToNext, goToPrevious]);

  // Mouse drag handlers for desktop swipe
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only left mouse button
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    hasDragged.current = false;
    mouseStartX.current = e.clientX;
    mouseEndX.current = null;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || mouseStartX.current === null) return;
    mouseEndX.current = e.clientX;
    // Mark as dragged if moved more than 10px
    if (Math.abs(mouseStartX.current - e.clientX) > 10) {
      hasDragged.current = true;
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    if (mouseStartX.current === null || mouseEndX.current === null) {
      mouseStartX.current = null;
      mouseEndX.current = null;
      return;
    }

    const diff = mouseStartX.current - mouseEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }

    mouseStartX.current = null;
    mouseEndX.current = null;
  }, [isDragging, goToNext, goToPrevious]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      mouseStartX.current = null;
      mouseEndX.current = null;
    }
  }, [isDragging]);

  // Handle click - only open lightbox if not dragging
  const handleImageClick = useCallback(() => {
    if (!hasDragged.current) {
      openLightbox();
    }
    hasDragged.current = false;
  }, [openLightbox]);

  // Calculate visible thumbnails for carousel
  const getVisibleThumbnails = () => {
    const maxVisible = 5;
    const total = images.length;

    if (total <= maxVisible) {
      return images.map((_, i) => i);
    }

    // Center current image in visible thumbnails
    let start = Math.max(0, currentIndex - Math.floor(maxVisible / 2));
    const end = Math.min(total, start + maxVisible);

    // Adjust start if we're near the end
    if (end - start < maxVisible) {
      start = Math.max(0, end - maxVisible);
    }

    return Array.from({ length: end - start }, (_, i) => start + i);
  };

  // Open lightbox at a specific image index
  const openLightboxAt = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsLightboxOpen(true);
  }, []);

  if (!images || images.length === 0) {
    return null;
  }

  const visibleThumbnails = getVisibleThumbnails();
  const remainingPhotos = Math.max(0, images.length - 3);

  return (
    <div className={styles.gallery} role="region" aria-label={title}>
      {/* ── MOBILE BENTO GRID (≤767px only) ── */}
      <div className={styles.bentoGrid}>
        {/* Main hero image */}
        <button
          type="button"
          className={styles.bentoHero}
          onClick={() => openLightboxAt(0)}
          aria-label={i18n.viewTitle(title, 1)}
        >
          <Image
            src={images[0]}
            alt={alts[0] || `${title} - Image 1`}
            fill
            sizes="100vw"
            className={styles.bentoImage}
            priority
          />
        </button>

        {/* Bottom row: 2 images side by side */}
        {images.length >= 2 && (
          <div className={styles.bentoRow}>
            <button
              type="button"
              className={styles.bentoCell}
              onClick={() => openLightboxAt(1)}
              aria-label={i18n.viewTitle(title, 2)}
            >
              <Image
                src={images[1]}
                alt={alts[1] || `${title} - Image 2`}
                fill
                sizes="50vw"
                className={styles.bentoImage}
              />
            </button>

            {images.length >= 3 && (
              <button
                type="button"
                className={styles.bentoCell}
                onClick={() => openLightboxAt(2)}
                aria-label={i18n.viewAll(images.length)}
              >
                <Image
                  src={images[2]}
                  alt={alts[2] || `${title} - Image 3`}
                  fill
                  sizes="50vw"
                  className={styles.bentoImage}
                />
                {/* "+N photos" overlay */}
                {remainingPhotos > 0 && (
                  <div className={styles.bentoOverlay}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                    <span>+{remainingPhotos}</span>
                  </div>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── DESKTOP: Bento mosaic grid (≥768px) ── */}
      <div className={styles.desktopGallery}>
        <div className={styles.desktopBento}>
          {/* Left: Main large image */}
          <button
            type="button"
            className={styles.desktopBentoMain}
            onClick={() => openLightboxAt(0)}
            aria-label={i18n.viewMain(title)}
          >
            <Image
              src={images[0]}
              alt={alts[0] || `${title} - Image 1`}
              fill
              sizes="(max-width: 1200px) 60vw, 50vw"
              className={styles.bentoImage}
              priority
            />
            <div className={styles.desktopBentoHover}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            </div>
          </button>

          {/* Right: 2×2 grid — up to 4 images (Airbnb pattern) */}
          {images.length >= 2 && (
            <div className={styles.desktopBentoSide}>
              {([1, 2, 3, 4] as const).map((idx) => {
                if (!images[idx]) return null;
                // Overlay on last visible cell when more than 5 total images exist
                const isLastVisible = idx === Math.min(4, images.length - 1);
                const showOverlay = isLastVisible && images.length > 5;
                return (
                  <button
                    key={idx}
                    type="button"
                    className={styles.desktopBentoCell}
                    onClick={() => openLightboxAt(idx)}
                    aria-label={showOverlay ? i18n.viewAll(images.length) : i18n.viewTitle(title, idx + 1)}
                  >
                    <Image
                      src={images[idx]}
                      alt={alts[idx] || `${title} - Image ${idx + 1}`}
                      fill
                      sizes="(min-width: 992px) 18vw, 30vw"
                      className={styles.bentoImage}
                    />
                    {showOverlay && (
                      <div className={styles.desktopBentoOverlay}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                        </svg>
                        <span>{i18n.viewAll(images.length)}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Thumbnail carousel strip (desktop only) */}
        {images.length > 1 && (
          <div className={styles.desktopThumbWrapper}>
            {thumbCanScrollLeft && (
              <button
                type="button"
                className={styles.desktopThumbArrow}
                onClick={() => scrollThumbs('left')}
                aria-label={i18n.prevImages}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            <div className={styles.desktopThumbStrip} ref={desktopThumbsRef}>
              {images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  className={`${styles.desktopThumb} ${i === currentIndex ? styles.desktopThumbActive : ''}`}
                  onClick={() => openLightboxAt(i)}
                  aria-label={i18n.viewImage(i + 1)}
                >
                  <Image
                    src={img}
                    alt={alts[i] || `${title} ${i + 1}`}
                    fill
                    sizes="88px"
                    className={styles.bentoImage}
                  />
                </button>
              ))}
            </div>
            {thumbCanScrollRight && (
              <button
                type="button"
                className={styles.desktopThumbArrow}
                onClick={() => scrollThumbs('right')}
                aria-label={i18n.nextImages}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Modal — rendered via portal at document.body to escape any stacking context */}
      {isLightboxOpen && createPortal(
        <div
          className={styles.lightbox}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={i18n.lightbox}
        >
          {/* Close Button — absolute over the whole lightbox */}
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            aria-label={i18n.close}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Content area: flex:1, centers image, contains nav arrows so they align to the image */}
          <div
            className={`${styles.lightboxContent} ${isDragging ? styles.dragging : ''}`}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {/* Image wrapper — arrows live here so top:50% = center of image */}
            <div className={styles.lightboxImageWrapper}>
              {/* Slide: overflow:hidden clips the slide animation */}
              <div
                key={slideKey}
                className={styles.lightboxSlide}
                data-dir={slideDir}
              >
                <Image
                  src={images[currentIndex]}
                  alt={alts[currentIndex] || `${title} - Image ${currentIndex + 1}`}
                  fill
                  sizes="100vw"
                  className={styles.lightboxImage}
                  priority
                />
              </div>

              {/* Nav arrows inside wrapper: top:50% of wrapper = top:50% of image (object-position:center) */}
              <button
                type="button"
                className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                aria-label={i18n.prev}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              <button
                type="button"
                className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                aria-label={i18n.next}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>

          {/* Counter (replaced by thumbnails on all viewports) */}
          <div className={styles.lightboxCounter} onClick={(e) => e.stopPropagation()}>
            {currentIndex + 1} / {images.length}
          </div>

          {/* Thumbnail strip — in-flow at bottom of flex column (NOT absolute anymore) */}
          {images.length > 1 && (
            <div
              className={styles.lightboxThumbs}
              ref={lightboxThumbsRef}
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  className={`${styles.lightboxThumb} ${i === currentIndex ? styles.lightboxThumbActive : ''}`}
                  onClick={(e) => { e.stopPropagation(); goToIndex(i); }}
                  aria-label={i18n.goTo(i + 1)}
                >
                  <Image
                    src={img}
                    alt=""
                    fill
                    sizes="64px"
                    className={styles.bentoImage}
                  />
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
